// src/components/CartDrawer.jsx
import React, { useState } from 'react';
import { FaTimes, FaTrash, FaPlus, FaMinus, FaShoppingBag, FaMotorcycle, FaUtensils, FaStore } from 'react-icons/fa';
import './CartDrawer.css';

const CartDrawer = ({ isOpen, onClose, cartItems, totalItems, subtotal, tax, total, onUpdateQuantity, onRemoveItem, onPlaceOrder, loading, tenant }) => {
  const primaryColor = tenant?.primary_color || '#FF9F0D';
  const [step, setStep] = useState('cart'); // 'cart' | 'checkout' | 'success'
  const [orderType, setOrderType] = useState('dine_in');
  const [submitting, setSubmitting] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    tableNumber: '',
    notes: '',
  });

  const handleFormChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePlaceOrder = async () => {
    if (!form.name || !form.phone) {
      alert('Please enter your name and phone number.');
      return;
    }
    setSubmitting(true);
    const order = await onPlaceOrder({ ...form, orderType });
    setSubmitting(false);
    if (order) {
      setPlacedOrder(order);
      setStep('success');
    } else {
      alert('Failed to place order. Please try again.');
    }
  };

  const handleClose = () => {
    if (step === 'success') {
      setStep('cart');
      setForm({ name: '', phone: '', email: '', tableNumber: '', notes: '' });
      setPlacedOrder(null);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="cart-overlay" onClick={handleClose} />

      {/* Drawer */}
      <div className="cart-drawer">
        {/* Header */}
        <div className="cart-header" style={{ borderBottomColor: `${primaryColor}30` }}>
          <div className="cart-header-left">
            <FaShoppingBag style={{ color: primaryColor }} />
            <h2>
              {step === 'cart' && `Your Cart${totalItems > 0 ? ` (${totalItems})` : ''}`}
              {step === 'checkout' && 'Checkout'}
              {step === 'success' && 'Order Placed! 🎉'}
            </h2>
          </div>
          <button className="cart-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        {/* ── CART STEP ── */}
        {step === 'cart' && (
          <>
            {cartItems.length === 0 ? (
              <div className="cart-empty">
                <div className="cart-empty-icon">🛒</div>
                <h3>Your cart is empty</h3>
                <p>Add items from the menu to get started</p>
              </div>
            ) : (
              <>
                <div className="cart-items-list">
                  {cartItems.map(item => (
                    <div key={item.id} className="cart-item">
                      {item.menu_items?.image_url && (
                        <img
                          src={item.menu_items.image_url}
                          alt={item.menu_items?.name}
                          className="cart-item-image"
                        />
                      )}
                      <div className="cart-item-details">
                        <h4 className="cart-item-name">{item.menu_items?.name}</h4>
                        {item.selected_variant?.name && (
                          <span className="cart-item-variant">{item.selected_variant.name}</span>
                        )}
                        {item.special_instructions && (
                          <span className="cart-item-note">📝 {item.special_instructions}</span>
                        )}
                        <div className="cart-item-bottom">
                          <span className="cart-item-price" style={{ color: primaryColor }}>
                            ₹{parseFloat(item.total_price).toFixed(2)}
                          </span>
                          <div className="cart-qty-controls">
                            <button
                              className="cart-qty-btn"
                              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                              style={{ borderColor: `${primaryColor}50`, color: primaryColor }}
                            >
                              <FaMinus />
                            </button>
                            <span className="cart-qty-value">{item.quantity}</span>
                            <button
                              className="cart-qty-btn"
                              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                              style={{ borderColor: `${primaryColor}50`, color: primaryColor }}
                            >
                              <FaPlus />
                            </button>
                          </div>
                          <button
                            className="cart-remove-btn"
                            onClick={() => onRemoveItem(item.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="cart-summary">
                  <div className="cart-summary-row">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-row">
                    <span>GST (5%)</span>
                    <span>₹{tax.toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-row cart-summary-total">
                    <span>Total</span>
                    <span style={{ color: primaryColor }}>₹{total.toFixed(2)}</span>
                  </div>

                  <button
                    className="cart-checkout-btn"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}
                    onClick={() => setStep('checkout')}
                  >
                    Proceed to Checkout →
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ── CHECKOUT STEP ── */}
        {step === 'checkout' && (
          <div className="cart-checkout-form">
            <button className="cart-back-btn" onClick={() => setStep('cart')}>
              ← Back to Cart
            </button>

            {/* Order Type */}
            <div className="checkout-section">
              <h3 className="checkout-section-title">Order Type</h3>
              <div className="order-type-grid">
                {[
                  { value: 'dine_in',  label: 'Dine In',  icon: <FaUtensils /> },
                  { value: 'takeaway', label: 'Takeaway', icon: <FaStore /> },
                  { value: 'delivery', label: 'Delivery', icon: <FaMotorcycle /> },
                ].map(type => (
                  <button
                    key={type.value}
                    className={`order-type-btn ${orderType === type.value ? 'active' : ''}`}
                    style={orderType === type.value ? {
                      borderColor: primaryColor,
                      background: `${primaryColor}15`,
                      color: primaryColor
                    } : {}}
                    onClick={() => setOrderType(type.value)}
                  >
                    {type.icon}
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Customer Details */}
            <div className="checkout-section">
              <h3 className="checkout-section-title">Your Details</h3>
              <div className="checkout-fields">
                <div className="checkout-field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    placeholder="Enter your name"
                  />
                </div>
                <div className="checkout-field">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="checkout-field">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleFormChange}
                    placeholder="your@email.com"
                  />
                </div>
                {orderType === 'dine_in' && (
                  <div className="checkout-field">
                    <label>Table Number</label>
                    <input
                      type="text"
                      name="tableNumber"
                      value={form.tableNumber}
                      onChange={handleFormChange}
                      placeholder="e.g. T-5"
                    />
                  </div>
                )}
                <div className="checkout-field checkout-field-full">
                  <label>Special Instructions</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleFormChange}
                    placeholder="Any special requests or allergies?"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="checkout-section">
              <h3 className="checkout-section-title">Order Summary</h3>
              <div className="checkout-items-summary">
                {cartItems.map(item => (
                  <div key={item.id} className="checkout-item-row">
                    <span className="checkout-item-qty">{item.quantity}x</span>
                    <span className="checkout-item-name">{item.menu_items?.name}</span>
                    <span className="checkout-item-price" style={{ color: primaryColor }}>
                      ₹{parseFloat(item.total_price).toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className="checkout-divider" />
                <div className="checkout-item-row checkout-total-row">
                  <span></span>
                  <span className="checkout-total-label">Total (incl. GST)</span>
                  <span className="checkout-total-value" style={{ color: primaryColor }}>
                    ₹{total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <button
              className="cart-place-order-btn"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}
              onClick={handlePlaceOrder}
              disabled={submitting}
            >
              {submitting ? 'Placing Order...' : `Place Order — ₹${total.toFixed(2)}`}
            </button>
          </div>
        )}

        {/* ── SUCCESS STEP ── */}
        {step === 'success' && placedOrder && (
          <div className="cart-success">
            <div className="success-animation">🎉</div>
            <h2 className="success-title">Order Placed!</h2>
            <p className="success-subtitle">Your order has been received and is being prepared</p>

            <div className="success-order-card" style={{ borderColor: `${primaryColor}40` }}>
              <div className="success-order-number" style={{ color: primaryColor }}>
                {placedOrder.order_number}
              </div>
              <div className="success-order-details">
                <div className="success-detail-row">
                  <span>Name</span>
                  <span>{form.name}</span>
                </div>
                <div className="success-detail-row">
                  <span>Phone</span>
                  <span>{form.phone}</span>
                </div>
                <div className="success-detail-row">
                  <span>Order Type</span>
                  <span className="success-order-type">{orderType.replace('_', ' ')}</span>
                </div>
                <div className="success-detail-row">
                  <span>Total</span>
                  <span style={{ color: primaryColor, fontWeight: 700 }}>
                    ₹{parseFloat(placedOrder.total_amount).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <p className="success-message">
              We'll notify you when your order is ready. Thank you for choosing {tenant?.business_name}!
            </p>

            <button
              className="success-close-btn"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}
              onClick={handleClose}
            >
              Continue Browsing
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
